/**
 * Pick from the Creative Library — client-side entity executor (ADR-037 D8).
 *
 * Runs in the HOST page, never in the plugin iframe. A plugin asks the host to
 * show its Creative Library picker; the host lists what the arche itself could
 * list (its own `list_items`, made with the arche's bridge token) and the
 * person picks one or closes the sheet. The plugin names at most a kind and
 * learns only the item picked.
 *
 * Host services arrive through `configureCreativePickerHost` at registration
 * (`client/src/lib/register-client-entities.ts`), because the executor context
 * carries only `{ userId, archeId, holderId }`.
 *
 * Expected failures are RESULTS, not throws, so a typed code reaches the SDK.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const CREATIVE_PICKER_ENTITY_ID = 'ent_intg_creative_picker_0000000';

export type CreativePickerKind = 'style' | 'character';

export type CreativePickerErrorCode = 'INVALID_INPUT' | 'PICKER_UNAVAILABLE' | 'SHEET_BUSY';

export interface CreativePickedItem {
  itemId: string;
  kind: CreativePickerKind;
  name: string;
  version: number;
}

export type CreativePickerResult =
  | { item: CreativePickedItem }
  | { canceled: true }
  | { error: { code: CreativePickerErrorCode; message: string } };

/** What the host page provides: the picker, drawn for one arche. */
export interface CreativePickerHost {
  showPicker(request: {
    archeId: string;
    kind: CreativePickerKind | null;
  }): Promise<CreativePickerResult>;
}

let host: CreativePickerHost | null = null;

/** Called once by the host page at registration. */
export function configureCreativePickerHost(next: CreativePickerHost | null): void {
  host = next;
}

const INVALID = (message: string): CreativePickerResult => ({
  error: { code: 'INVALID_INPUT', message },
});

export async function creativePickerExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<CreativePickerResult> {
  let kind: CreativePickerKind | null = null;
  if (input !== undefined && input !== null) {
    if (typeof input !== 'object' || Array.isArray(input)) {
      return INVALID('The picker takes { kind? }');
    }
    const { kind: asked, ...rest } = input as Record<string, unknown>;
    if (Object.keys(rest).length > 0) return INVALID('The picker takes only { kind? }');
    if (asked !== undefined) {
      if (asked !== 'style' && asked !== 'character') {
        return INVALID("kind is 'style' or 'character'");
      }
      kind = asked;
    }
  }
  if (!host) {
    return {
      error: {
        code: 'PICKER_UNAVAILABLE',
        message: 'The Creative Library picker is not available here',
      },
    };
  }
  return host.showPicker({ archeId: context.archeId, kind });
}

export const creativePickerDefinition = {
  entityId: CREATIVE_PICKER_ENTITY_ID,
  rateLimitPerMinute: 10,
  pluginPermission: 'vault:creative:read',
  executor: creativePickerExecutor,
} as const satisfies ClientEntityDefinition<unknown, CreativePickerResult>;
